import React, { useState, useEffect } from "react";
import { UxBackground, loadSpeechstream } from "@imaginelearning/mps-ux-component-library";
import {
  IFixedformAttempt,
  AlpService,
  INextActivity,
  LinkService,
  AttemptRequestBody
} from "@imaginelearning/mps-api-service";
import { useLocation } from "react-router-dom";
import useGradeRange from "../../hooks/usegraderange";
import useUser from "../../hooks/useuser";
import useTheme from "../../hooks/usetheme";
import useSubject from "../../hooks/usesubject";
import { ILinkPlayerProps, TLessonLocationState } from "./link-launch-types";
import LinkPlayerWithModals from "./linkplayerwithmodals";
import useProductOriginDomain from "../../hooks/linkplayer/useProductOriginDomain";

function LinkLaunch(props: ILinkPlayerProps) {
  const { is6to12 } = useGradeRange();
  const { user, language, gradeBand, avatar, background } = useUser();
  const { theme } = useTheme();

  const [alpId, setAlpId] = useState<string>("");
  const [lessonPathId, setLessonPathId] = useState<string>("");
  const [lessonPathVersion, setLessonPathVersion] = useState<number>(0);
  const { setSubject } = useSubject();
  const [calculatorType, setCalculatorType] = useState<string>("");
  const { domain } = useProductOriginDomain();
  const location = useLocation();

  const ctkEnvId = window.STUDENT_ENV?.REACT_APP_FIXEDFORM_PLAYER_CTK_ENV_ID;
  const ctkEnvUrl = window.STUDENT_ENV?.REACT_APP_FIXEDFORM_PLAYER_CTK_ENV_URL;
  // Uncomment to use QA lessons instead of Production lessons
  // const ctkEnvId=12;
  // const ctkEnvUrl="https://edg-qa2.doolittle-prod.wna.cloud";

  const getCurrentState = () => {
    const params = new URLSearchParams(location.search);
    let pathId = params.get("lesson") ?? "";
    let pathVersion = Number(params.get("version") ?? "0");
    let ilpSubject = params.get("subject") ?? "Math";
    let alpId = params.get("assignment") ?? params.get("alpId") ?? "";

    const webState = location.state as TLessonLocationState;
    const calculatorType = webState?.calculatorType ?? "";

    if (webState) {
      pathId = webState.lessonPathId;
      pathVersion = webState.lessonPathVersion;
      ilpSubject = webState.ilpSubject;
      alpId = webState.alpId;
    }
    setSubject(ilpSubject);
    setAlpId(alpId);
    setLessonPathId(pathId);
    setLessonPathVersion(pathVersion);
    setCalculatorType(calculatorType);
  };

  const getNextActivity = async (
    pathId: string = lessonPathId,
    pathVersion: number = lessonPathVersion,
    assignmentId: string = alpId
  ) => {
    const { response } = await AlpService.getNextActivity(pathId, pathVersion, assignmentId);
    return response as INextActivity;
  };

  const createAttempt = async (nextTake: INextActivity) => {
    const requestBody: AttemptRequestBody = {
      pathId: `${nextTake.pathID}`,
      attemptId: "takeID" in nextTake ? nextTake.takeID : "",
      pathVersion: `${nextTake.version}`,
      ctkEnvUrl: ctkEnvUrl as string,
      ctkEnvId: ctkEnvId as number,
      metadata: {
        stateForStandards: "",
        assignmentId: (nextTake as { assignmentID?: string }).assignmentID ?? "",
        tags: `ABLD|Mastery|${nextTake.assignmentID}`
      },
      introMode: "None",
      outroMode: "None",
      language: language ?? "en",
      productOriginDomain: domain
    };

    // Force pagination to assist with testing
    const overridePagination = localStorage.getItem("overridePagination");
    if (overridePagination) {
      requestBody.paginationMode = overridePagination;
    }

    const res = await LinkService.createAttempt(user.id, requestBody);
    return res.response as IFixedformAttempt;
  };

  const updateStatus = async (attemptId: string, status: string) => {
    const res = await AlpService.updateByTakeId(attemptId, status, user.schoolYear, gradeBand);
    return res;
  };

  useEffect(() => {
    const initialLoad = async () => {
      loadSpeechstream();
      getCurrentState();
    };
    initialLoad();
  }, []);

  return (
    <div className="mps-link-player">
      <UxBackground
        background={background}
        className={`position-fixed z-index--1 ${is6to12 ? "background-right-bottom" : ""}`}
        theme={theme}
      />
      <LinkPlayerWithModals
        lessonId={lessonPathId}
        lessonVersion={lessonPathVersion}
        assignmentId={alpId}
        avatar={avatar}
        ctkEnvId={ctkEnvId}
        ctkEnvUrl={ctkEnvUrl}
        useScoreFromService={true}
        calculatorType={calculatorType}
        updateStatus={updateStatus}
        getNextActivity={getNextActivity}
        createAttempt={createAttempt}
      />
    </div>
  );
}

export default LinkLaunch;
